if (!customElements.get('color-swatches')) {
  customElements.define('color-swatches', class extends HTMLElement {
    connectedCallback() {
      try { this.currentMap = JSON.parse(this.dataset.currentMap || '{}'); }
      catch (e) { this.currentMap = {}; }

      this.optionNames = (this.dataset.optionNames || '').split(',').filter(Boolean);
      this.nameEl = this.querySelector('.cs-swatches__name');
      this.bindHover();
      this.initTabs();

      // Fires on size/fit changes AND on the "only available" toggle
      this.onChange = () => setTimeout(() => this.update(), 50);
      document.addEventListener('change', this.onChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(() => this.update(), 50));
      }

      this.update();
      this.syncSleeveStyle();
      // the theme re-renders the picker on every size/fit change
      requestAnimationFrame(() => this.syncSleeveStyle());
    }

    // Copy the look of the theme's Size/Fit buttons onto the Sleeve buttons
    syncSleeveStyle() {
      if (!this.querySelector('.cs-sleeve')) return;
      const scope = this.closest('.shopify-section') || document;
      const picker = scope.querySelector('variant-picker') || document.querySelector('variant-picker');
      if (!picker) return;

      let normal = null, selected = null;
      picker.querySelectorAll('label.block-swatch').forEach((label) => {
        const input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
        if (input && input.checked) { if (!selected) selected = label; }
        else if (!normal && !label.classList.contains('is-disabled') && !label.classList.contains('cf-unavailable')) normal = label;
      });
      const base = normal || selected;
      if (!base) return;

      const border = (cs) => `${cs.borderTopWidth} ${cs.borderTopStyle} ${cs.borderTopColor}`;
      const b = getComputedStyle(base);
      const vars = {
        '--sl-height': b.height,
        '--sl-min-width': b.minWidth,
        '--sl-padding': `${b.paddingTop} ${b.paddingRight} ${b.paddingBottom} ${b.paddingLeft}`,
        '--sl-border': border(b),
        '--sl-radius': b.borderRadius,
        '--sl-bg': b.backgroundColor,
        '--sl-shadow': b.boxShadow,
        '--sl-color': b.color,
        '--sl-font-family': b.fontFamily,
        '--sl-font-size': b.fontSize,
        '--sl-font-weight': b.fontWeight,
        '--sl-letter-spacing': b.letterSpacing,
        '--sl-text-transform': b.textTransform
      };
      const list = base.parentElement && getComputedStyle(base.parentElement);
      if (list && list.columnGap && list.columnGap !== 'normal') vars['--sl-gap'] = list.columnGap;

      if (selected) {
        const s = getComputedStyle(selected);
        vars['--sl-sel-border'] = border(s);
        vars['--sl-sel-shadow'] = s.boxShadow;
        vars['--sl-sel-bg'] = s.backgroundColor;
        vars['--sl-sel-color'] = s.color;
      }
      Object.entries(vars).forEach(([k, v]) => { if (v) this.style.setProperty(k, v); });
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
      if (this.unsub) this.unsub();
    }

    handleize(str) {
      return String(str).toLowerCase().trim()
        .replace(/['"]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    // Same remembered state as the "Only show options available" toggle
    onlyAvailableOn() {
      try {
        const stored = sessionStorage.getItem('cf-only-available');
        if (stored !== null) return stored === '1';
      } catch (e) {}
      if (typeof window.__cfOnlyAvailState === 'boolean') return window.__cfOnlyAvailState;
      const toggle = document.getElementById('cf-only-available');
      return !!(toggle && toggle.checked);
    }

    // Build the key from the buttons actually selected (works even if the combo doesn't exist)
    getSelectedKey() {
      if (!this.optionNames.length) return null;
      const scope = this.closest('.shopify-section') || document;
      const picker = scope.querySelector('variant-picker') || document.querySelector('variant-picker');
      if (!picker) return null;

      const selects = picker.querySelectorAll('select');
      const parts = [];

      for (let i = 0; i < this.optionNames.length; i++) {
        const pos = i + 1;
        let val = null;

        const checked = picker.querySelector(`input[type="radio"][data-option-position="${pos}"]:checked`);
        if (checked) {
          const label = picker.querySelector(`label[for="${checked.id}"]`);
          const span = label ? label.querySelector('span') : null;
          val = (span && span.textContent.trim()) || checked.value;
        } else if (selects[i]) {
          val = selects[i].value;
        }

        if (!val) return null;
        parts.push(this.optionNames[i] + '-' + this.handleize(val));
      }
      return parts.join('_');
    }

    getVariantId() {
      const scope = this.closest('.shopify-section') || document;
      const input = scope.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && input.value) return input.value;
      return new URL(window.location.href).searchParams.get('variant');
    }

    update() {
      const key = this.getSelectedKey() || this.currentMap[this.getVariantId()];
      const strict = this.onlyAvailableOn();

      this.querySelectorAll('.cs-swatch, a.cs-sleeve').forEach((sw) => {
        const isCurrent = sw.classList.contains('is-selected');
        let unavailable = sw.classList.contains('is-unavailable');

        if (key) {
          unavailable = !sw.classList.contains('avail--' + key);
          sw.classList.toggle('is-unavailable', unavailable);
        }

        // Current color: show the line only, no link or fading
        if (isCurrent) return;

        if (key) {
          let map = {};
          try { map = JSON.parse(sw.dataset.variants || '{}'); } catch (e) {}
          const id = map[key];
          sw.href = id ? `${sw.dataset.url}?variant=${id}` : sw.dataset.url;
        }

        // Visual only – swatches always stay clickable
        sw.classList.toggle('is-blocked', strict && unavailable);
      });
    }

    // ---------- Category tabs ----------
    initTabs() {
      this.tabs = this.querySelectorAll('[data-cs-tab]');
      if (!this.tabs.length) return;

      // Remember the chosen tab while on this page (survives theme re-renders on size/fit change)
      window.__csActiveTab = window.__csActiveTab || {};
      const groupId = this.dataset.group || 'default';
      const saved = window.__csActiveTab[groupId];
      if (saved && this.querySelector(`[data-cs-tab="${saved}"]`)) this.showTab(saved);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          const name = tab.dataset.csTab;
          window.__csActiveTab[groupId] = name;
          this.showTab(name);
        });
      });
    }

    showTab(name) {
      this.tabs.forEach((tab) => {
        const on = tab.dataset.csTab === name;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.querySelectorAll('.cs-swatch').forEach((sw) => {
        const show = name === 'all' || sw.classList.contains('cs-cat--' + name);
        sw.classList.toggle('is-hidden', !show);
      });
    }

    bindHover() {
      if (!this.nameEl) return;
      this.querySelectorAll('.cs-swatch:not(.is-selected)').forEach((sw) => {
        sw.addEventListener('mouseenter', () => { this.nameEl.textContent = sw.getAttribute('aria-label'); });
        sw.addEventListener('mouseleave', () => { this.nameEl.textContent = this.nameEl.dataset.default; });
      });
    }
  });
}